import React, { useEffect, useState } from "react";
import {
  Box,
  Paper,
  Grid,
  TextField,
  MenuItem,
  Button,
  Typography,
  Table,
  TableHead,
  TableRow,
  TableCell,
  TableBody,
  Divider,
  Alert,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
} from "@mui/material";
import axios from "axios";

interface ClassModel { id: string; name: string; }
interface SectionModel { id: string; name: string; }
interface StudentModel { id: string; firstName: string; lastName: string; admissionNumber: string; }
interface PaymentMode { id: string; name: string; }

interface DemandCell {
  feeHeadId: string;
  feeInstallmentId: string;
  payable: number;
  paid: number;
  due: number;
}
interface DemandResponse {
  studentId: string;
  feeStructureId: string;
  totalAmount: number;
  paidAmount: number;
  dueAmount: number;
  breakdown: DemandCell[];
}

const FeeCollection: React.FC = () => {
  const [classes, setClasses] = useState<ClassModel[]>([]);
  const [sections, setSections] = useState<SectionModel[]>([]);
  const [students, setStudents] = useState<StudentModel[]>([]);
  const [paymentModes, setPaymentModes] = useState<PaymentMode[]>([]);
  const [classId, setClassId] = useState("");
  const [sectionId, setSectionId] = useState("");
  const [studentId, setStudentId] = useState("");
  const [demand, setDemand] = useState<DemandResponse | null>(null);
  const [error, setError] = useState<string | null>(null);
  const [collectOpen, setCollectOpen] = useState(false);
  const [collectAmounts, setCollectAmounts] = useState<Record<string, number>>({});
  const [totalPaid, setTotalPaid] = useState(0);
  const [paymentModeId, setPaymentModeId] = useState("");
  const [remarks, setRemarks] = useState("");
  const [receipt, setReceipt] = useState<any | null>(null);

  useEffect(() => {
    (async () => {
      try {
        const [cls, pm] = await Promise.all([
          axios.get("/system-settings/classes"),
          axios.get("/system-settings/payment-mode?isActive=true"),
        ]);
        setClasses(cls.data);
        setPaymentModes(pm.data);
      } catch (e) {
        setError("Failed to load masters");
      }
    })();
  }, []);

  useEffect(() => {
    if (!classId) {
      setSections([]);
      setSectionId("");
      return;
    }
    (async () => {
      try {
        const res = await axios.get("/system-settings/section", { params: { classId } });
        setSections(res.data);
      } catch {
        setError("Failed to load sections");
      }
    })();
  }, [classId]);

  useEffect(() => {
    if (!classId || !sectionId) {
      setStudents([]);
      setStudentId("");
      return;
    }
    (async () => {
      try {
        const res = await axios.get("/students", { params: { classId, sectionId, isActive: true } });
        setStudents(res.data.items || res.data); // handle {items,total} or array
      } catch {
        setError("Failed to load students");
      }
    })();
  }, [classId, sectionId]);

  const fetchDemand = async () => {
    if (!studentId) return;
    try {
      const res = await axios.get(`/fee-collection/demand/${studentId}`);
      setDemand(res.data);
      setCollectAmounts({});
      setTotalPaid(0);
      setReceipt(null);
      setCollectOpen(true);
    } catch (e: any) {
      setError(e.response?.data?.message || "Failed to get demand");
    }
  };

  const setAmount = (cellKey: string, value: string, due: number) => {
    const num = Number(value || 0);
    const val = Math.max(0, Math.min(num, Number(due)));
    setCollectAmounts(prev => ({ ...prev, [cellKey]: val }));
    const newTotal = Object.values({ ...collectAmounts, [cellKey]: val }).reduce((s, v) => s + (Number(v) || 0), 0);
    setTotalPaid(newTotal);
  };

  const submitCollection = async () => {
    try {
      if (!paymentModeId) {
        alert("Payment mode is required");
        return;
      }
      const lines = (demand?.breakdown || []).map(b => {
        const key = `${b.feeHeadId}:${b.feeInstallmentId}`;
        const amount = Number(collectAmounts[key] || 0);
        return amount > 0 ? ({
          feeHeadId: b.feeHeadId,
          feeInstallmentId: b.feeInstallmentId,
          amount,
        }) : null;
      }).filter(Boolean) as Array<{feeHeadId: string; feeInstallmentId: string; amount: number;}>;
      if (lines.length === 0) {
        alert("Enter at least one amount");
        return;
      }
      const payload = {
        studentId,
        totalPaid,
        paymentDate: new Date().toISOString().slice(0,10),
        paymentModeId,
        remarks,
        lines,
      };
      const res = await axios.post("/fee-collection/collect", payload);
      setReceipt(res.data);
      // refresh demand
      const d = await axios.get(`/fee-collection/demand/${studentId}`);
      setDemand(d.data);
    } catch (e: any) {
      alert(e.response?.data?.message || "Failed to collect");
    }
  };

  return (
    <Paper sx={{ p: 2 }}>
      <Typography variant="h6" sx={{ mb: 2 }}>Fee Collection</Typography>
      {error && <Alert severity="error" sx={{ mb: 2 }}>{error}</Alert>}
      <Grid container spacing={2}>
        <Grid item xs={12} sm={4}>
          <TextField select label="Class" value={classId} onChange={e => setClassId(e.target.value)} fullWidth>
            {classes.map(c => <MenuItem key={c.id} value={c.id}>{c.name}</MenuItem>)}
          </TextField>
        </Grid>
        <Grid item xs={12} sm={4}>
          <TextField select label="Section" value={sectionId} onChange={e => setSectionId(e.target.value)} fullWidth>
            {sections.map(s => <MenuItem key={s.id} value={s.id}>{s.name}</MenuItem>)}
          </TextField>
        </Grid>
        <Grid item xs={12} sm={4}>
          <TextField select label="Student" value={studentId} onChange={e => setStudentId(e.target.value)} fullWidth>
            {students.map(s => <MenuItem key={s.id} value={s.id}>{s.admissionNumber} - {s.firstName} {s.lastName}</MenuItem>)}
          </TextField>
        </Grid>
        <Grid item xs={12}>
          <Button variant="contained" onClick={fetchDemand} disabled={!studentId}>Load Demand</Button>
        </Grid>
      </Grid>

      <Dialog open={collectOpen} onClose={() => setCollectOpen(false)} maxWidth="lg" fullWidth>
        <DialogTitle>Fee Demand & Collection</DialogTitle>
        <DialogContent>
          {demand && (
            <>
              <Box sx={{ display: "flex", gap: 4, mb: 2 }}>
                <Typography>Total: {demand.totalAmount.toFixed(2)}</Typography>
                <Typography>Paid: {demand.paidAmount.toFixed(2)}</Typography>
                <Typography>Due: {demand.dueAmount.toFixed(2)}</Typography>
              </Box>
              <Table size="small">
                <TableHead>
                  <TableRow>
                    <TableCell>Fee Head</TableCell>
                    <TableCell>Installment</TableCell>
                    <TableCell align="right">Payable</TableCell>
                    <TableCell align="right">Paid</TableCell>
                    <TableCell align="right">Due</TableCell>
                    <TableCell align="right">Collect</TableCell>
                  </TableRow>
                </TableHead>
                <TableBody>
                  {demand.breakdown.map(b => (
                    <TableRow key={`${b.feeHeadId}:${b.feeInstallmentId}`}>
                      <TableCell>{b.feeHeadId}</TableCell>
                      <TableCell>{b.feeInstallmentId}</TableCell>
                      <TableCell align="right">{b.payable.toFixed(2)}</TableCell>
                      <TableCell align="right">{b.paid.toFixed(2)}</TableCell>
                      <TableCell align="right">{b.due.toFixed(2)}</TableCell>
                      <TableCell align="right">
                        <TextField
                          type="number"
                          size="small"
                          value={collectAmounts[`${b.feeHeadId}:${b.feeInstallmentId}`] || ""}
                          onChange={(e) => setAmount(`${b.feeHeadId}:${b.feeInstallmentId}`, e.target.value, b.due)}
                          InputProps={{ inputProps: { min: 0 } }}
                          sx={{ width: 100 }}
                        />
                      </TableCell>
                    </TableRow>
                  ))}
                </TableBody>
              </Table>

              <Divider sx={{ my: 2 }} />
              <Grid container spacing={2}>
                <Grid item xs={12} sm={4}>
                  <TextField select label="Payment Mode" value={paymentModeId} onChange={e => setPaymentModeId(e.target.value)} fullWidth>
                    {paymentModes.map(pm => <MenuItem key={pm.id} value={pm.id}>{pm.name}</MenuItem>)}
                  </TextField>
                </Grid>
                <Grid item xs={12} sm={4}>
                  <TextField label="Remarks" value={remarks} onChange={e => setRemarks(e.target.value)} fullWidth />
                </Grid>
                <Grid item xs={12} sm={4}>
                  <TextField label="Total Paid" value={totalPaid.toFixed(2)} InputProps={{ readOnly: true }} fullWidth />
                </Grid>
                <Grid item xs={12}>
                  <Button variant="contained" onClick={submitCollection}>Submit Collection</Button>
                </Grid>
              </Grid>

              {receipt && (
                <Box sx={{ mt: 2 }}>
                  <Typography variant="subtitle1">Receipt No: {receipt.receiptNo}</Typography>
                  <Typography variant="body2">Payment Date: {new Date(receipt.paymentDate).toLocaleDateString()}</Typography>
                  <Typography variant="body2">Mode: {receipt.paymentMode?.name}</Typography>
                  <Typography variant="body2">Total Paid: {receipt.totalPaid}</Typography>
                </Box>
              )}
            </>
          )}
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setCollectOpen(false)}>Close</Button>
        </DialogActions>
      </Dialog>
    </Paper>
  );
};

export default FeeCollection;
